/**
 * SortButton - Sort selector with bottom sheet
 *
 * Composed component: Depends on BottomSheet from core.
 * Button with icon that opens bottom sheet with sort options.
 */

import { useState } from 'react';
import { Check, LucideIcon, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BottomSheet } from '../core/BottomSheet';

export interface SortOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

export interface SortButtonProps {
  /** Available sort options */
  options: SortOption[];
  /** Currently selected value */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Bottom sheet title */
  sheetTitle?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Default icon */
  icon?: LucideIcon;
}

export function SortButton({
  options,
  value,
  onChange,
  sheetTitle = 'Sort by',
  disabled = false,
  icon: Icon = ArrowUpDown,
}: SortButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <>
      {/* Trigger button - icon only */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={cn(
          'p-3 rounded-full bg-white flex-shrink-0',
          'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
          'text-slate-600 active:bg-slate-50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-sky-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-label={sheetTitle}
      >
        <Icon className="h-5 w-5" />
      </button>

      {/* Bottom sheet */}
      <BottomSheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={sheetTitle}
      >
        <div className="space-y-1">
          {options.map((option) => {
            const isSelected = option.value === value;
            const OptionIcon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-4 rounded-xl',
                  'text-left text-base transition-colors',
                  'active:bg-slate-100',
                  isSelected
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-slate-800 hover:bg-slate-50'
                )}
              >
                {OptionIcon && (
                  <OptionIcon className={cn(
                    'h-5 w-5',
                    isSelected ? 'text-sky-500' : 'text-slate-600'
                  )} />
                )}
                <span className="flex-1">{option.label}</span>
                {isSelected && <Check className="h-5 w-5" />}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
